import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import ScoreDonut from "./ScoreDonut";
import { bandColor, PROGRESS_TABS, shortMonth } from "./reportMeta";

const AgeCircle = ({ value, caption, note, tint, testId }) => (
  <div className="flex flex-col items-center text-center flex-1 min-w-[110px]" data-testid={testId}>
    <div
      className="w-[104px] h-[104px] rounded-full flex flex-col items-center justify-center border"
      style={{ background: `${tint}14`, borderColor: `${tint}44` }}
    >
      <span className="font-editorial text-3xl leading-none">{value ?? "—"}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Years</span>
    </div>
    <p className="text-xs font-medium mt-2">{caption}</p>
    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 max-w-[160px]">{note}</p>
  </div>
);

export default function LongevityOverview({ report }) {
  const [tab, setTab] = useState("overall");
  const active = PROGRESS_TABS.find((t) => t.key === tab) || PROGRESS_TABS[0];

  const chartData = (report.sessions || []).map((s) => ({
    label: shortMonth(s.date),
    date: s.date,
    overall: s.overall,
    functional_age_years: s.functional_age_years,
    brain_age_years: s.brain_age_years,
    key_biomarkers: s.key_biomarkers,
  }));

  const overallBand = bandColor(report.overall?.band);

  return (
    <section className="space-y-4" data-testid="longevity-overview-section">
      {/* Score + age profile */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="rounded-2xl lg:col-span-2" data-testid="longevity-score-card">
          <CardContent className="p-6 flex flex-col items-center text-center">
            <p className="tracking-overline text-[11px] text-muted-foreground self-start mb-3">
              COCOON LONGEVITY SCORE
            </p>
            <ScoreDonut
              value={report.overall?.score ?? null}
              max={100}
              size={186}
              stroke={18}
              color={overallBand.ring}
              testId="longevity-overall-donut"
            />
            <span
              className="mt-3 inline-block px-4 py-1 rounded-full text-xs font-semibold tracking-wide"
              style={{ background: overallBand.bg, color: overallBand.fg }}
              data-testid="longevity-overall-band"
            >
              {report.overall?.band}
            </span>
            <p className="text-xs text-muted-foreground mt-3 max-w-[300px]" data-testid="longevity-overall-sentence">
              {report.overall?.sentence}
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl lg:col-span-3" data-testid="longevity-age-profile-card">
          <CardContent className="p-6">
            <p className="tracking-overline text-[11px] text-muted-foreground mb-5">YOUR AGE PROFILE</p>
            <div className="flex flex-wrap gap-4 justify-around">
              <AgeCircle
                value={report.ages?.chronological}
                caption="Chronological Age"
                note="Your actual age"
                tint="#b67358"
                testId="longevity-age-chronological"
              />
              <AgeCircle
                value={report.ages?.functional}
                caption="Cocoon Biometric 360"
                note={report.ages?.functional_label}
                tint="#2e8b63"
                testId="longevity-age-functional"
              />
              <AgeCircle
                value={report.ages?.brain}
                caption="Brain Age"
                note={report.ages?.brain_label}
                tint="#6c5ea8"
                testId="longevity-age-brain"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress over time */}
      <Card className="rounded-2xl" data-testid="longevity-progress-card">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="tracking-overline text-[11px] text-muted-foreground">YOUR PROGRESS OVER TIME</p>
            <div className="inline-flex rounded-full border border-border overflow-hidden">
              {PROGRESS_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 text-[11px] transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid={`longevity-progress-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[230px]" data-testid="longevity-progress-chart">
            {chartData.length >= 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 16, left: -14, bottom: 0 }}>
                  <CartesianGrid stroke="#e7e4dd" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                  <Line
                    type="monotone"
                    dataKey={active.dataKey}
                    name={active.label}
                    stroke={active.color}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: active.color }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-muted-foreground py-10 text-center" data-testid="longevity-progress-empty">
                Your progress line appears here once your first measurements are saved below.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Improvements + next focus */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl" data-testid="longevity-improvements-card">
          <CardContent className="p-6">
            <p className="tracking-overline text-[11px] text-muted-foreground mb-4">KEY IMPROVEMENTS</p>
            {(report.improvements || []).length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="longevity-improvements-empty">
                Your changes since baseline appear here after your second set of measurements.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {report.improvements.map((i) => (
                  <div key={i.label} data-testid={`longevity-improvement-${i.label.replace(/\W+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center gap-1.5">
                      {i.improved
                        ? <ArrowUpRight className="w-4 h-4 text-emerald-700" />
                        : <ArrowDownRight className="w-4 h-4 text-rose-700" />}
                      <span className={`font-editorial text-2xl ${i.improved ? "text-emerald-700" : "text-rose-700"}`}>
                        {i.display}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{i.label}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl" data-testid="longevity-next-focus-card">
          <CardContent className="p-6">
            <p className="tracking-overline text-[11px] text-muted-foreground mb-4">YOUR NEXT FOCUS</p>
            {(report.next_focus || []).length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="longevity-next-focus-empty">
                Your focus areas appear here once we have your first diagnostic scores.
              </p>
            ) : (
              <ol className="space-y-3">
                {report.next_focus.map((f, idx) => (
                  <li key={f.key} className="flex items-start gap-3" data-testid={`longevity-next-focus-${f.key}`}>
                    <span className="w-6 h-6 rounded-full bg-accent/15 text-accent text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium leading-tight">{f.area}</p>
                      <p className="text-[11px] text-muted-foreground">{f.advice}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
